import React, { useState } from 'react';
import { Tv } from 'lucide-react';
import { CTVAdsContent } from '../types/ads.types';
import MediaUploader from '../components/MediaUploader';
import PlatformEditorTabs from '../components/PlatformEditorTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface CTVEditorProps {
  content: CTVAdsContent;
  isEditing: boolean;
  onChange: (field: keyof CTVAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function CTVEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: CTVEditorProps) {
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [currentCtaIndex, setCurrentCtaIndex] = useState(0);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [showAllCtas, setShowAllCtas] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetTab, setActiveAssetTab] = useState('videos');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    content.platforms || ['roku', 'amazon_fire']
  );

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  // Initialize variations if they don't exist
  const headlineVariations = content.headlineVariations || [content.headline || ''];
  const ctaVariations = content.callToActionVariations || ['Learn More', 'Watch Now'];

  const updateHeadlineVariations = (variations: string[]) => {
    onChange('headlineVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('headline', variations[currentHeadlineIndex] || '');
  };

  const updateCtaVariations = (variations: string[]) => {
    onChange('callToActionVariations', variations);
  };

  const getAssetSubTabs = () => {
    const tabs = [];

    // Videos are primary for CTV
    tabs.push({ id: 'videos', label: 'Videos' });

    // Text for overlays
    tabs.push({ id: 'text', label: 'Text Overlays' });

    // Companion banners
    tabs.push({ id: 'images', label: 'Companion Banners' });

    // QR codes for interactive experiences
    tabs.push({ id: 'qr', label: 'QR Codes' });

    return tabs;
  };

  const handlePlatformToggle = (platform: string) => {
    const newPlatforms = selectedPlatforms.includes(platform)
      ? selectedPlatforms.filter(p => p !== platform)
      : [...selectedPlatforms, platform];

    setSelectedPlatforms(newPlatforms);
    onChange('platforms', newPlatforms);
  };

  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Tv className="w-5 h-5 mr-2 text-purple-600" />
            Connected TV Campaign Settings
          </h3>

          <div className="space-y-4">
            {/* Campaign Name - Full Width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                value={content.campaignName || ''}
                onChange={(e) => onChange('campaignName', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Enter campaign name..."
              />
            </div>

            {/* Platforms - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CTV Platforms</label>
              <div className="space-y-2">
                {[
                  { id: 'roku', label: 'Roku' },
                  { id: 'amazon_fire', label: 'Amazon Fire TV' },
                  { id: 'apple_tv', label: 'Apple TV' },
                  { id: 'samsung', label: 'Samsung TV Plus' },
                  { id: 'hulu', label: 'Hulu' },
                  { id: 'youtube_tv', label: 'YouTube TV' },
                  { id: 'peacock', label: 'Peacock' },
                  { id: 'paramount', label: 'Paramount+' }
                ].map((platform) => (
                  <label key={platform.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.id)}
                      onChange={() => handlePlatformToggle(platform.id)}
                      disabled={!isEditing}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    <span className="ml-2 text-sm text-gray-700">{platform.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Video Specs */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video Duration</label>
              <select
                value={content.videoSpecs?.duration || 30}
                onChange={(e) => onChange('videoSpecs', { ...content.videoSpecs, duration: parseInt(e.target.value) })}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value={6}>6 seconds (Bumper)</option>
                <option value={15}>15 seconds</option>
                <option value={30}>30 seconds</option>
                <option value={60}>60 seconds</option>
                <option value={90}>90 seconds</option>
              </select>
            </div>

            {/* Video Resolution */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video Resolution</label>
              <select
                value={content.videoSpecs?.resolution || '1920x1080'}
                onChange={(e) => onChange('videoSpecs', { ...content.videoSpecs, resolution: e.target.value })}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="1920x1080">1920x1080 (Full HD)</option>
                <option value="3840x2160">3840x2160 (4K UHD)</option>
                <option value="1280x720">1280x720 (HD)</option>
              </select>
            </div>

            {/* Ad Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ad Format</label>
              <select
                value={content.adFormat || 'standard'}
                onChange={(e) => onChange('adFormat', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="standard">Standard Video</option>
                <option value="interactive">Interactive Video</option>
                <option value="shoppable">Shoppable TV</option>
                <option value="branded_content">Branded Content</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-6">
          {/* Asset Sub-tabs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b">
              <div className="flex space-x-1 p-1">
                {getAssetSubTabs().map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveAssetTab(tab.id)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeAssetTab === tab.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Videos */}
              {activeAssetTab === 'videos' && (
                <div>
                  <MediaUploader
                    creatives={content.creatives?.filter(c => c.type === 'video') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.creatives?.filter(c => c.type !== 'video') || [];
                      onChange('creatives', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="Connected TV"
                    acceptedTypes={['video/mp4', 'video/mov', 'video/webm']}
                    maxFiles={5}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />

                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">CTV Video Requirements:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Resolution: 1920x1080 (HD) or 3840x2160 (4K)</li>
                      <li>• Duration: 6, 15, 30, 60, or 90 seconds</li>
                      <li>• Format: MP4, MOV, or WebM</li>
                      <li>• Aspect Ratio: 16:9 (landscape)</li>
                      <li>• File Size: Under 500MB recommended</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Text Overlays */}
              {activeAssetTab === 'text' && (
                <div className="space-y-6">
                  {/* Headline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Headline</label>
            {isEditing && (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newVariations = [...headlineVariations, ''];
                    updateHeadlineVariations(newVariations);
                    setCurrentHeadlineIndex(newVariations.length - 1);
                  }}
                  className="px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                >
                  + Add Variable
                </button>
                <button
                  onClick={() => setShowAllHeadlines(!showAllHeadlines)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                >
                  {showAllHeadlines ? 'Show Single' : 'Show All'}
                </button>
              </div>
            )}
          </div>

          {showAllHeadlines ? (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {headlineVariations.map((text, index) => (
                <div key={index} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">CTV Headline {index + 1}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{text.length}/60</span>
                      {isEditing && headlineVariations.length > 1 && (
                        <button
                          onClick={() => {
                            const newVariations = headlineVariations.filter((_, i) => i !== index);
                            updateHeadlineVariations(newVariations);
                            setCurrentHeadlineIndex(Math.max(0, Math.min(currentHeadlineIndex, newVariations.length - 1)));
                          }}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => {
                      const newVariations = [...headlineVariations];
                      newVariations[index] = e.target.value;
                      updateHeadlineVariations(newVariations);
                    }}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    maxLength={60}
                    placeholder={`CTV headline variable ${index + 1}...`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div>
              <VariationToggle
                variations={headlineVariations}
                label={`Current: Variable ${currentHeadlineIndex + 1} of ${headlineVariations.length}`}
                currentIndex={currentHeadlineIndex}
                onIndexChange={setCurrentHeadlineIndex}
                isEditing={isEditing}
                maxCharLimit={60}
                showAllVariations={false}
              />
              {isEditing && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={headlineVariations[currentHeadlineIndex] || ''}
                    onChange={(e) => {
                      const newVariations = [...headlineVariations];
                      newVariations[currentHeadlineIndex] = e.target.value;
                      updateHeadlineVariations(newVariations);
                    }}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength={60}
                    placeholder="Enter headline variation..."
                  />
                </div>
              )}
            </div>
                      )}
                </div>
              )}

              {/* Companion Banners */}
              {activeAssetTab === 'images' && (
                <div>
                  <MediaUploader
                    creatives={content.companionBanners?.filter(c => c.type === 'image') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.companionBanners?.filter(c => c.type !== 'image') || [];
                      onChange('companionBanners', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="CTV Companion"
                    acceptedTypes={['image/*']}
                    maxFiles={10}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />

                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-sm font-medium text-green-800 mb-2">Companion Banner Specs:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Common Sizes: 300x250, 728x90, 320x50</li>
                      <li>• Format: JPEG, PNG, GIF</li>
                      <li>• File Size: Under 150KB per banner</li>
                      <li>• Click-through URL supported</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* QR Codes */}
              {activeAssetTab === 'qr' && (
                <div>
                  <MediaUploader
                    creatives={content.qrCodes?.filter(c => c.type === 'image') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.qrCodes?.filter(c => c.type !== 'image') || [];
                      onChange('qrCodes', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="CTV QR Code"
                    acceptedTypes={['image/*']}
                    maxFiles={5}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />

                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="text-sm font-medium text-purple-800 mb-2">QR Code Requirements:</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• Resolution: Minimum 500x500 pixels</li>
                      <li>• Format: PNG, JPEG, or SVG</li>
                      <li>• High contrast for TV display</li>
                      <li>• Consider viewing distance (10+ feet)</li>
                      <li>• Include clear call-to-action text</li>
                      <li>• Test scanability from TV distance</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">TV Audience Targeting</h3>
          <div className="space-y-4">
            {/* Household Demographics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Household Income</label>
              <select
                value={content.householdIncome || 'all'}
                onChange={(e) => onChange('householdIncome', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all">All Income Levels</option>
                <option value="0-50k">$0 - $50,000</option>
                <option value="50k-75k">$50,000 - $75,000</option>
                <option value="75k-100k">$75,000 - $100,000</option>
                <option value="100k-150k">$100,000 - $150,000</option>
                <option value="150k+">$150,000+</option>
              </select>
            </div>

            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
              <select
                value={content.ageRange || 'all'}
                onChange={(e) => onChange('ageRange', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all">All Ages</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55-64">55-64</option>
                <option value="65+">65+</option>
              </select>
            </div>

            {/* Genre Targeting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content Genres</label>
              <input
                type="text"
                value={content.genres || ''}
                onChange={(e) => onChange('genres', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Sports, News, Drama, Comedy, Reality TV"
              />
            </div>

            {/* Dayparting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daypart Targeting</label>
              <select
                value={content.daypart || 'all_day'}
                onChange={(e) => onChange('daypart', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all_day">All Day</option>
                <option value="early_morning">Early Morning (6AM-9AM)</option>
                <option value="daytime">Daytime (9AM-4PM)</option>
                <option value="early_fringe">Early Fringe (4PM-7PM)</option>
                <option value="prime_time">Prime Time (7PM-11PM)</option>
                <option value="late_night">Late Night (11PM-2AM)</option>
                <option value="overnight">Overnight (2AM-6AM)</option>
              </select>
            </div>

            {/* Geographic Targeting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geographic Targeting</label>
              <input
                type="text"
                value={content.geoTargeting || ''}
                onChange={(e) => onChange('geoTargeting', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., United States, California, Los Angeles DMA"
              />
            </div>

            {/* Device Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Device Types</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.deviceTypes?.includes('smart_tv') || false}
                    onChange={(e) => {
                      const types = content.deviceTypes || [];
                      if (e.target.checked) {
                        onChange('deviceTypes', [...types, 'smart_tv']);
                      } else {
                        onChange('deviceTypes', types.filter(t => t !== 'smart_tv'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Smart TVs</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.deviceTypes?.includes('streaming_device') || false}
                    onChange={(e) => {
                      const types = content.deviceTypes || [];
                      if (e.target.checked) {
                        onChange('deviceTypes', [...types, 'streaming_device']);
                      } else {
                        onChange('deviceTypes', types.filter(t => t !== 'streaming_device'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Streaming Devices (Roku, Fire TV, etc.)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.deviceTypes?.includes('gaming_console') || false}
                    onChange={(e) => {
                      const types = content.deviceTypes || [];
                      if (e.target.checked) {
                        onChange('deviceTypes', [...types, 'gaming_console']);
                      } else {
                        onChange('deviceTypes', types.filter(t => t !== 'gaming_console'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Gaming Consoles</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Budget & Delivery</h3>
          <div className="space-y-4">
            {/* Budget Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Type</label>
              <select
                value={content.budgetType || 'impressions'}
                onChange={(e) => onChange('budgetType', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="impressions">CPM (Cost per 1000 Impressions)</option>
                <option value="completed_views">CPCV (Cost per Completed View)</option>
                <option value="reach">CPR (Cost per Reach)</option>
              </select>
            </div>

            {/* Campaign Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Budget ($)</label>
              <input
                type="number"
                value={content.budget || ''}
                onChange={(e) => onChange('budget', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="5000"
                step="1000"
                placeholder="Minimum $5,000"
              />
            </div>

            {/* CPM Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target CPM ($)</label>
              <input
                type="number"
                value={content.targetCPM || ''}
                onChange={(e) => onChange('targetCPM', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="15"
                step="5"
                placeholder="Typical: $15-$65"
              />
            </div>

            {/* Impression Goal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Impression Goal</label>
              <input
                type="number"
                value={content.impressionGoal || ''}
                onChange={(e) => onChange('impressionGoal', parseInt(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="100000"
                step="100000"
                placeholder="e.g., 1,000,000"
              />
            </div>

            {/* Frequency Cap */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency Cap</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={content.frequencyCap || ''}
                  onChange={(e) => onChange('frequencyCap', parseInt(e.target.value) || 0)}
                  disabled={!isEditing}
                  className="w-20 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  min="1"
                  placeholder="3"
                />
                <span className="text-sm text-gray-600">impressions per</span>
                <select
                  value={content.frequencyPeriod || 'day'}
                  onChange={(e) => onChange('frequencyPeriod', e.target.value)}
                  disabled={!isEditing}
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="campaign">Campaign</option>
                </select>
              </div>
            </div>

            {/* Campaign Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flight Dates</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={content.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={content.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Estimated Reach */}
            {content.budget && content.targetCPM && (
              <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">Estimated Campaign Reach</h4>
                <p className="text-2xl font-bold text-purple-700">
                  {((content.budget / content.targetCPM) * 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} impressions
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Based on ${content.budget} budget at ${content.targetCPM} CPM
                </p>
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <p className="text-sm font-medium text-purple-800">Estimated Household Reach:</p>
                  <p className="text-lg font-semibold text-purple-700">
                    {((content.budget / content.targetCPM) * 1000 / (content.frequencyCap || 3)).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} unique households
                  </p>
                  <p className="text-xs text-purple-600">
                    With frequency cap of {content.frequencyCap || 3} impressions
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}